import type { Country } from './country/types.js';

/**
 * Buyer / org URL slug — shared by the API (which computes it once from the
 * registry name), the web canonical, and the prerender/sitemap, so all three
 * agree on the exact string. INN-FIRST: `{tin}-{name-slug}`.
 *
 * WHY INN-first (not name-first like the tender slug). The ИНН is the buyer's
 * stable public key and is always a valid bare URL on its own, so `/buyer/{tin}`
 * (every URL already indexed) stays a clean prefix of the slugged form and keeps
 * resolving — {@link extractOrgTin} recovers the ИНН from the leading digits with
 * no dependence on the name. The name is decoration for the crawler and the
 * click; the ИНН is the identity.
 *
 * The name half is derived from the REGISTRY name only (not the source-fallback
 * display name), because that is the one value the profile endpoint and the
 * `/buyers/index` list resolve identically — decoupling the slug from the
 * fallback is what guarantees the canonical, the sitemap entry and the
 * prerendered file are byte-identical URLs. A buyer with no registry name gets
 * the bare `{tin}` (exactly its URL today — no migration for that subset).
 */

// Cyrillic (Russian + Uzbek) → Latin, for a readable ASCII slug. Multi-char
// first so `щ`/`ю` etc. resolve before the single-letter pass.
const TRANSLIT: ReadonlyArray<readonly [string, string]> = [
  ['щ', 'shch'],
  ['ю', 'yu'],
  ['я', 'ya'],
  ['ж', 'zh'],
  ['ч', 'ch'],
  ['ш', 'sh'],
  ['ё', 'yo'],
  ['ъ', ''],
  ['ь', ''],
  ['ў', "o'"],
  ['қ', 'q'],
  ['ғ', "g'"],
  ['ҳ', 'h'],
  ['а', 'a'],
  ['б', 'b'],
  ['в', 'v'],
  ['г', 'g'],
  ['д', 'd'],
  ['е', 'e'],
  ['з', 'z'],
  ['и', 'i'],
  ['й', 'y'],
  ['к', 'k'],
  ['л', 'l'],
  ['м', 'm'],
  ['н', 'n'],
  ['о', 'o'],
  ['п', 'p'],
  ['р', 'r'],
  ['с', 's'],
  ['т', 't'],
  ['у', 'u'],
  ['ф', 'f'],
  ['х', 'x'],
  ['ц', 'ts'],
  ['ы', 'i'],
  ['э', 'e'],
];

// Legal-form words dropped from the slug — they carry no keyword value and,
// leading, would eat the whole length cap («общество с ограниченной…»). Matched
// as whole tokens after transliteration, so both Cyrillic-origin and native
// Latin spellings are covered. Kept lowercase, apostrophes stripped.
const STOPWORDS = new Set([
  // ru abbreviations + transliterated phrase words
  'ooo',
  'oao',
  'zao',
  'ao',
  'gup',
  'dup',
  'up',
  'gp',
  'chp',
  'sp',
  'fh',
  'obshchestvo',
  's',
  'ogranichennoy',
  'ogranichennoi',
  'otvetstvennostyu',
  'aktsionernoe',
  'aksionernoe',
  'otkrytoe',
  'zakrytoe',
  'gosudarstvennoe',
  'unitarnoe',
  'predpriyatie',
  'chastnoe',
  'sovmestnoe',
  'semeynoe',
  // uz Latin
  'mchj',
  'aj',
  'oaj',
  'duk',
  'xk',
  'ok',
  'yatt',
  'masuliyati',
  'cheklangan',
  'jamiyati',
  'jamiyat',
  'aksiyadorlik',
  'ochiq',
  'yopiq',
  'davlat',
  'unitar',
  'korxonasi',
  'korxona',
  'xususiy',
  'oilaviy',
  'fermer',
  'xojaligi',
  'qoshma',
]);

/**
 * Kyrgyz letters, on a KG deployment only. The table above knows none of them, so
 * `.replace(/[^a-z0-9]+/g, ' ')` cut every Kyrgyz word at them: «Өкмөтү» became
 * `km-t`, «Түштүк» `t-sht-k` (KZ lessons #40 — slugs are fixed once indexed, so
 * this had to land before the first KG crawl). Applied before the table, as the
 * tender slug's `toLatin` reads them (`ң`→`ng`, `ө`→`o`, `ү`→`u`). UZ and KZ keep
 * their slugs byte for byte: a Kazakh name with these letters is not re-slugged.
 */
const KYRGYZ_TRANSLIT: ReadonlyArray<readonly [string, string]> = [
  ['ң', 'ng'],
  ['ө', 'o'],
  ['ү', 'u'],
];

const MAX_SLUG_LEN = 60;

// Kyrgyz legal forms (ManasTender, 2026-11-05) — dropped ONLY on a kg deployment, so a
// UZ/KZ slug never changes (their pages are indexed): ОсОО, ОсДО, ЖЧК «жоопкерчилиги
// чектелген коом», МУП, МП, ГУ. ОАО/ЗАО/ГП/ИП/ОО are already in STOPWORDS.
const KG_STOPWORDS = new Set([
  'osoo',
  'osdo',
  'zhchk',
  'zhoopkerchiligi',
  'chektelgen',
  'koom',
  'mup',
  'mp',
  'gu',
]);

function transliterate(input: string, country?: Country): string {
  let s = input.toLowerCase();
  // Fold apostrophe variants so `o'zbek` → `ozbek` (the apostrophe is dropped by
  // slugification anyway; folding first keeps `o'` and `oʻ` identical).
  s = s.replace(/[ʻʼ'’`´]/g, '');
  if (country === 'kg') {
    for (const [from, to] of KYRGYZ_TRANSLIT) s = s.split(from).join(to);
  }
  for (const [from, to] of TRANSLIT) s = s.split(from).join(to);
  return s;
}

/**
 * The name half of the slug: transliterated, legal-form-stripped, hyphenated.
 * `country` — the deployment (`activeCountry()` on the API); only `kg` changes
 * anything (Kyrgyz letters, see {@link KYRGYZ_TRANSLIT}).
 */
export function slugifyOrgName(name: string | null | undefined, country?: Country): string {
  if (!name) return '';
  const tokens = transliterate(name, country)
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(
      (tok) => tok.length > 0 && !STOPWORDS.has(tok) && !(country === 'kg' && KG_STOPWORDS.has(tok)),
    );
  let slug = '';
  for (const tok of tokens) {
    const next = slug ? `${slug}-${tok}` : tok;
    if (next.length > MAX_SLUG_LEN) break;
    slug = next;
  }
  return slug;
}

/** Full buyer path slug segment: `{tin}-{name}`, or bare `{tin}` when the name
 *  slugifies to nothing (no registry name, or all stopwords). */
export function orgSlug(tin: string, name: string | null | undefined, country?: Country): string {
  const base = slugifyOrgName(name, country);
  return base ? `${tin}-${base}` : tin;
}

/**
 * Recover the ИНН from a `/buyer/:seg` (or `/supplier/:seg`) route param that
 * may be slugged (`{tin}-{name}`) or bare (`{tin}`). The ИНН is the LEADING run
 * of digits — an org name never starts with the buyer's own ИНН, and the slug is
 * INN-first by construction. Returns the whole param when it has no leading
 * digits (defensive; never happens for a real buyer URL).
 */
export function extractOrgTin(param: string): string {
  const m = param.match(/^(\d+)/);
  return m ? m[1] : param;
}
